import { Box, Chip, TableCell, Tooltip, Typography } from "@mui/material";
import { Schedule as ScheduleIcon } from "@mui/icons-material";
import { ReservedSlot } from "../types";
import { WEEK_DAY_LABELS } from "../../../utils/reservedSlotsConstants";
import { ServiceProvided } from "../../treatments/treatment-prescription/types";
import { activeServicesProvidedFilter, plannedServicesProvidedFilter } from "../../treatments/treatment-prescription/utils";

type ReservedSlotsTableCellProps = {
    reservedSlots: Array<ReservedSlot> | undefined;
    serviceProvided: ServiceProvided;
};

const getWeekDayLabel = (weekDay: number) => {
    return WEEK_DAY_LABELS[weekDay] ?? String(weekDay);
};

const getShortWeekDayLabel = (weekDay: number) => {
    const fullLabel = getWeekDayLabel(weekDay);
    return fullLabel.substring(0, 3);
};

const formatTime = (time: string) => {
    if (!time) return "";
    const parts = time.split(":");
    const formattedTime = `${parts[0]}:${parts[1]}`;
    return formattedTime;
};

export const ReservedSlotsTableCell = ({ reservedSlots, serviceProvided }: ReservedSlotsTableCellProps) => {
    const isActiveOrPlanned = activeServicesProvidedFilter(serviceProvided) || plannedServicesProvidedFilter(serviceProvided);
    const isMonthlyOnce = serviceProvided.session_periodicity === "monthly" && serviceProvided.session_quantity === 1;
    const isNotApplicable = serviceProvided.session_periodicity === "not_applicable";
    const reservationNotRequired = isMonthlyOnce || isNotApplicable;

    if (!reservedSlots || reservedSlots.length === 0) {
        if (reservationNotRequired) {
            return (
                <TableCell align="center">
                    <Chip
                        label="N/A"
                        size="small"
                        color="primary"
                        sx={{ fontSize: "0.75rem", height: "24px" }}
                    />
                </TableCell>
            );
        }

        return (
            <TableCell align="center">
                {isActiveOrPlanned ? (
                    <Chip
                        label="Sem reserva"
                        size="small"
                        color="error"
                        sx={{ fontSize: "0.75rem", height: "24px" }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        -
                    </Typography>
                )}
            </TableCell>
        );
    }

    const sortedReservedSlots = [...reservedSlots].sort((a, b) => a.week_day - b.week_day);

    const tooltipContent = sortedReservedSlots.map((slot) => {
        const therapistName = slot.therapist
            ? `${slot.therapist.first_name} ${slot.therapist.last_name}`
            : null;
        const dayLabel = getWeekDayLabel(slot.week_day);
        const time = formatTime(slot.time);
        const slotText = therapistName
            ? `${dayLabel} ${time} - ${therapistName}`
            : `${dayLabel} ${time}`;
        return slotText;
    });

    return (
        <TableCell align="center">
            <Tooltip
                title={
                    <Box>
                        {tooltipContent.map((text, index) => (
                            <Typography key={index} variant="body2">
                                {text}
                            </Typography>
                        ))}
                    </Box>
                }
                arrow
            >
                <Box display="flex" flexDirection="row" gap={0.5} justifyContent="center" flexWrap="wrap">
                    {sortedReservedSlots.map((slot) => (
                        <Chip
                            key={slot.id}
                            icon={<ScheduleIcon sx={{ fontSize: "14px !important" }} />}
                            label={`${getShortWeekDayLabel(slot.week_day)} ${formatTime(slot.time)}`}
                            size="small"
                            color="primary"
                            sx={{ fontSize: "0.75rem", height: "24px" }}
                        />
                    ))}
                </Box>
            </Tooltip>
        </TableCell>
    );
};

